import { useCallback, useEffect, useRef, useState } from "react"
import { useMutation } from "@tanstack/react-query"
import type { API } from "@/api/client"
import {
    requestNotificationPermission,
    resetRecommendationNotificationQueue,
    showRecommendationNotification,
    showWelcomeNotification,
} from "@/lib/browserNotifications"
import { getNotificationPrefs } from "@/lib/notificationPrefs"
import type { CopilotSummary } from "../types"
import { CHECKPOINT_ORDER } from "../components/CopilotPanel/CheckpointList"
import {
    assertNever,
    parseCopilotStreamEvent,
    type RecommendationItem,
} from "../copilotEvents"
import { settled } from "@/shared/lib/settled"

export type RecommendationPriority = RecommendationItem["priority"]

export interface CopilotRecommendation extends RecommendationItem {
    id: string
    chunkIndex: number
    elapsedSec: number
    receivedAt: number
}

export type CopilotCheckpoints = Record<string, 0 | 1>

function emptyCheckpoints(): CopilotCheckpoints {
    return Object.fromEntries(CHECKPOINT_ORDER.map((key) => [key, 0])) as CopilotCheckpoints
}

function parseCheckpoints(raw: unknown): CopilotCheckpoints | null {
    if (!raw || typeof raw !== "object") return null
    const next = emptyCheckpoints()
    for (const key of CHECKPOINT_ORDER) {
        const value = (raw as Record<string, unknown>)[key]
        if (value === 1) next[key] = 1
    }
    return next
}

interface UseCopilotOptions {
    /** Колбэк для побочных событий из SSE (например, visit_card_update). */
    onSseEvent?: (data: { type?: string; [k: string]: unknown }) => void
    /** Вызывается когда finalize сессии завершён (SSE done или поллинг), независимо от успеха загрузки summary. */
    onSessionDone?: (sessionId: string) => void
    /** Вызывается с готовым summary, ключуется по sessionId которая его породила. */
    onSummary?: (sessionId: string, summary: CopilotSummary) => void
    /** Summary retries exhausted for this session (no summary available). */
    onSummaryFailed?: (sessionId: string) => void
    isRecording?: boolean
}

const SUMMARY_FETCH_ATTEMPTS = 5
const SUMMARY_FETCH_BASE_DELAY_MS = 1000
const DONE_POLL_ATTEMPTS = 60
const DONE_POLL_DELAY_MS = 5000
const SSE_RECONNECT_ATTEMPTS = 5
const SSE_RECONNECT_BASE_DELAY_MS = 2000

async function fetchCopilotSummaryWithRetry(
    apiClient: API<unknown>,
    sessionId: string,
): Promise<CopilotSummary | null> {
    for (let attempt = 0; attempt < SUMMARY_FETCH_ATTEMPTS; attempt++) {
        const result = await settled(apiClient.v1.getCopilotSessionSummary(sessionId))
        if (result.ok && result.value.data) {
            return result.value.data as CopilotSummary
        }
        // 404 while summary is being written — retry
        if (attempt < SUMMARY_FETCH_ATTEMPTS - 1) {
            await new Promise((r) => setTimeout(r, SUMMARY_FETCH_BASE_DELAY_MS * (attempt + 1)))
        }
    }
    return null
}

export function useCopilot(api: API<unknown>, options: UseCopilotOptions = {}) {
    const [isActive, setIsActive] = useState(false)
    const [recommendations, setRecommendations] = useState<CopilotRecommendation[]>([])
    const [checkpoints, setCheckpoints] = useState<CopilotCheckpoints>(emptyCheckpoints)
    const [showMissedCheckpoints, setShowMissedCheckpoints] = useState(false)
    const [keepPanelAfterStop, setKeepPanelAfterStop] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [copilotSessionId, setCopilotSessionId] = useState<string | null>(null)
    const copilotSessionIdRef = useRef<string | null>(null)
    const eventSourceRef = useRef<EventSource | null>(null)
    const doneSessionsRef = useRef<Set<string>>(new Set())
    // ref на колбэк, чтобы новые версии не пересоздавали EventSource.
    const onSseEventRef = useRef(options.onSseEvent)
    const onSessionDoneRef = useRef(options.onSessionDone)
    const onSummaryRef = useRef(options.onSummary)
    const onSummaryFailedRef = useRef(options.onSummaryFailed)
    const isRecordingRef = useRef(options.isRecording ?? false)
    useEffect(() => {
        onSseEventRef.current = options.onSseEvent
        onSessionDoneRef.current = options.onSessionDone
        onSummaryRef.current = options.onSummary
        onSummaryFailedRef.current = options.onSummaryFailed
        isRecordingRef.current = options.isRecording ?? false
    }, [
        options.onSseEvent,
        options.onSessionDone,
        options.onSummary,
        options.onSummaryFailed,
        options.isRecording,
    ])

    const markSessionDone = useCallback((sessionId: string): boolean => {
        if (doneSessionsRef.current.has(sessionId)) return false
        doneSessionsRef.current.add(sessionId)
        onSessionDoneRef.current?.(sessionId)
        return true
    }, [])

    const connectEventsRef = useRef<(sessionId: string, attempt?: number) => void>(() => {})
    const connectEvents = useCallback(
        (sessionId: string, attempt = 0) => {
            const baseUrl = api.instance.defaults.baseURL ?? ""
            const es = new EventSource(`${baseUrl}/v1/copilot/sessions/${sessionId}/events`, {
                withCredentials: true,
            })
            eventSourceRef.current = es

            es.onmessage = (event: MessageEvent) => {
                const parsed = parseCopilotStreamEvent(event.data as string)
                if (parsed === null) return
                switch (parsed.kind) {
                    case "connected":
                        return
                    case "done": {
                        es.close()
                        if (eventSourceRef.current === es) eventSourceRef.current = null
                        setIsActive(false)
                        if (markSessionDone(sessionId)) {
                            void fetchCopilotSummaryWithRetry(api, sessionId).then((summaryData) => {
                                if (summaryData) {
                                    onSummaryRef.current?.(sessionId, summaryData)
                                } else {
                                    onSummaryFailedRef.current?.(sessionId)
                                }
                            })
                        }
                        return
                    }
                    // Все «чужие» типы событий (например, visit_card_update от
                    // отдельного visit_card_service) — наружу через колбэк.
                    case "foreign":
                        onSseEventRef.current?.(
                            parsed.data as { type?: string; [k: string]: unknown },
                        )
                        return
                    case "recommendations": {
                        const nextCheckpoints = parseCheckpoints(parsed.event.checkpoints)
                        if (nextCheckpoints) setCheckpoints(nextCheckpoints)
                        if (parsed.event.recommendations.length > 0 && isRecordingRef.current) {
                            const newRecs: CopilotRecommendation[] = parsed.event.recommendations.map(
                                (r) => ({
                                    ...r,
                                    id: crypto.randomUUID(),
                                    chunkIndex: parsed.event.chunk_index,
                                    elapsedSec: parsed.event.elapsed_sec,
                                    receivedAt: Date.now(),
                                }),
                            )
                            setRecommendations((prev) => [...newRecs, ...prev])

                            const top =
                                newRecs.find((r) => r.priority === "high") ??
                                newRecs.find((r) => r.priority === "medium") ??
                                newRecs[0]
                            const notifPrefs = getNotificationPrefs()
                            if (notifPrefs.enabled) {
                                showRecommendationNotification(top, {
                                    format: notifPrefs.format,
                                })
                            }
                        }
                        return
                    }
                    default:
                        return assertNever(parsed)
                }
            }

            es.onerror = () => {
                es.close()
                if (eventSourceRef.current !== es) return
                eventSourceRef.current = null
                const sessionGone =
                    copilotSessionIdRef.current !== sessionId ||
                    doneSessionsRef.current.has(sessionId)
                if (sessionGone || attempt >= SSE_RECONNECT_ATTEMPTS) {
                    setIsActive(false)
                    return
                }
                window.setTimeout(() => {
                    if (
                        copilotSessionIdRef.current === sessionId &&
                        !doneSessionsRef.current.has(sessionId) &&
                        eventSourceRef.current === null
                    ) {
                        connectEventsRef.current(sessionId, attempt + 1)
                    }
                }, SSE_RECONNECT_BASE_DELAY_MS * (attempt + 1))
            }
        },
        [api, markSessionDone],
    )
    useEffect(() => {
        connectEventsRef.current = connectEvents
    }, [connectEvents])

    const { mutateAsync: createSessionAsync } = useMutation({
        mutationFn: async (profileName: string) => {
            const response = await api.v1.createCopilotSession({
                profile_name: profileName,
            })
            return response.data
        },
        onError: () => {
            setError("Не удалось запустить Copilot")
        },
    })

    const startCopilot = useCallback(
        async (profileId: string, opts?: { adopt?: boolean }): Promise<string | null> => {
            void requestNotificationPermission()

            // Close any previous EventSource before starting a new session
            if (eventSourceRef.current) {
                eventSourceRef.current.close()
                eventSourceRef.current = null
            }
            try {
                let sessionId: string | null
                if (opts?.adopt) {
                    sessionId = profileId
                } else {
                    const data = await createSessionAsync(profileId)
                    sessionId = data?.session_id ?? null
                }
                if (!sessionId) return null

                copilotSessionIdRef.current = sessionId
                setCopilotSessionId(sessionId)
                resetRecommendationNotificationQueue()
                setRecommendations([])
                // Session-local: always start this meeting from zeros (ignore profile OR).
                setCheckpoints(emptyCheckpoints())
                setShowMissedCheckpoints(false)
                setKeepPanelAfterStop(false)
                setError(null)

                connectEvents(sessionId)

                setIsActive(true)
                return sessionId
            } catch {
                return null
            }
        },
        [connectEvents, createSessionAsync],
    )

    /**
     * Страховка от потери одноразового SSE done: после остановки записи
     * поллим summary до готовности и завершаем сессию независимо от SSE.
     */
    const watchSessionDone = useCallback(
        (sessionId: string) => {
            void (async () => {
                for (let attempt = 0; attempt < DONE_POLL_ATTEMPTS; attempt++) {
                    await new Promise((r) => setTimeout(r, DONE_POLL_DELAY_MS))
                    if (doneSessionsRef.current.has(sessionId)) return
                    const result = await settled(api.v1.getCopilotSessionSummary(sessionId))
                    if (result.ok && result.value.data) {
                        if (markSessionDone(sessionId)) {
                            onSummaryRef.current?.(sessionId, result.value.data as CopilotSummary)
                        }
                        return
                    }
                    // summary ещё не готов — продолжаем поллинг
                }
                if (markSessionDone(sessionId)) {
                    onSummaryFailedRef.current?.(sessionId)
                }
            })()
        },
        [api, markSessionDone],
    )

    const stopCopilot = useCallback(() => {
        eventSourceRef.current?.close()
        eventSourceRef.current = null
        copilotSessionIdRef.current = null
        setIsActive(false)
    }, [])

    const clearRecommendations = useCallback(() => {
        setRecommendations([])
        setShowMissedCheckpoints(false)
        setKeepPanelAfterStop(false)
        setCheckpoints(emptyCheckpoints())
    }, [])

    /** Inject recommendations without SSE (demo script playback). No browser notifications. */
    const appendRecommendations = useCallback((recs: CopilotRecommendation[]) => {
        if (recs.length === 0) return
        setRecommendations((prev) => [...recs, ...prev])
    }, [])

    /** Mark demo/scripted checkpoints as done (OR with existing 1s). */
    const applyCheckpoints = useCallback((keys: string[]) => {
        if (keys.length === 0) return
        setCheckpoints((prev) => {
            const next = { ...prev }
            for (const key of keys) {
                if (key in next) next[key] = 1
            }
            return next
        })
    }, [])

    /** Reset checkpoint UI for a new demo playback run. */
    const resetCheckpointsForDemo = useCallback(() => {
        setCheckpoints(emptyCheckpoints())
        setShowMissedCheckpoints(false)
    }, [])

    const markMissedCheckpoints = useCallback(() => {
        setShowMissedCheckpoints(true)
    }, [])

    /** Keep Copilot panel after live recording stop; show missed until clear / new start. */
    const markLiveRecordingStopped = useCallback(() => {
        setShowMissedCheckpoints(true)
        setKeepPanelAfterStop(true)
    }, [])

    useEffect(() => {
        return () => {
            eventSourceRef.current?.close()
            eventSourceRef.current = null
        }
    }, [])

    useEffect(() => {
        if (!options.isRecording || !isActive) return

        const onVisibilityChange = () => {
            if (!document.hidden) return
            const notifPrefs = getNotificationPrefs()
            if (!notifPrefs.enabled) return
            showWelcomeNotification()
        }

        document.addEventListener("visibilitychange", onVisibilityChange)
        return () => document.removeEventListener("visibilitychange", onVisibilityChange)
    }, [options.isRecording, isActive])

    return {
        isActive,
        recommendations,
        checkpoints,
        showMissedCheckpoints,
        keepPanelAfterStop,
        error,
        copilotSessionId,
        copilotSessionIdRef,
        startCopilot,
        stopCopilot,
        watchSessionDone,
        clearRecommendations,
        appendRecommendations,
        applyCheckpoints,
        resetCheckpointsForDemo,
        markMissedCheckpoints,
        markLiveRecordingStopped,
    }
}
